// frontend/TwitterAnalytics/modules/UserAnalysis.jsx
/**
 * Module 2: User-Wise Analysis
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, IconButton, Avatar,
  Card, CardContent, Dialog, DialogTitle, DialogContent,
  CircularProgress, Tooltip, Tabs, Tab, List, ListItem,
  ListItemText, Divider, Alert
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Person as PersonIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  Visibility as ViewIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import axiosInstance from "../../../utils/axiosInstance";

// User Category Badge Component
const CategoryBadge = ({ category }) => {
  const config = {
    super_fan: { color: 'success', icon: <StarIcon fontSize="small" />, label: 'Super Fan' },
    critic: { color: 'error', icon: <WarningIcon fontSize="small" />, label: 'Critic' },
    regular: { color: 'primary', icon: <PersonIcon fontSize="small" />, label: 'Regular' },
    new_user: { color: 'info', icon: <PersonIcon fontSize="small" />, label: 'New' }
  };
  const cfg = config[category] || config.new_user;
  return <Chip icon={cfg.icon} label={cfg.label} size="small" color={cfg.color} />;
};

// Sentiment Bar Component
const SentimentBar = ({ positive, negative, neutral }) => {
  const total = positive + negative + neutral;
  if (total === 0) return <Typography variant="caption">No data</Typography>;
  
  const pctPos = (positive / total) * 100;
  const pctNeg = (negative / total) * 100;
  const pctNeu = (neutral / total) * 100;

  return (
    <Box sx={{ width: '100%' }}>
      <Box display="flex" height={8} borderRadius={1} overflow="hidden">
        <Box sx={{ width: `${pctPos}%`, bgcolor: 'success.main' }} />
        <Box sx={{ width: `${pctNeu}%`, bgcolor: 'grey.400' }} />
        <Box sx={{ width: `${pctNeg}%`, bgcolor: 'error.main' }} />
      </Box>
      <Box display="flex" justifyContent="space-between" mt={0.5}>
        <Typography variant="caption" color="success.main">{positive}</Typography>
        <Typography variant="caption" color="text.secondary">{neutral}</Typography>
        <Typography variant="caption" color="error.main">{negative}</Typography>
      </Box>
    </Box>
  );
};

const UserAnalysis = ({ apiTier, onShowSnackbar }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Users list state
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({ page: 1, per_page: 20, total: 0 });
  const [usersSearch, setUsersSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Top interactors state
  const [topInteractors, setTopInteractors] = useState([]);
  const [topSummary, setTopSummary] = useState(null);
  
  // User profile dialog
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userJourney, setUserJourney] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 0) {
      fetchTopInteractors();
    } else {
      fetchUsers();
    }
  }, [activeTab, categoryFilter]);

  useEffect(() => {
    fetchUsers();
  }, [usersPagination.page, usersSearch]);

  const fetchTopInteractors = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/twitter/users/top', {
        params: {
          category: categoryFilter,
          limit: 20
        }
      });
      const response = res.data;
      if (response.success) {
        setTopInteractors(response.data);
        setTopSummary(response.summary);
      }
    } catch (err) {
      onShowSnackbar('Failed to fetch top interactors', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/twitter/users', {
        params: {
          page: usersPagination.page,
          per_page: usersPagination.per_page,
          search: usersSearch,
          category: categoryFilter
        }
      });
      const response = res.data;
      if (response.success) {
        setUsers(response.data);
        setUsersPagination(prev => ({ ...prev, total: response.pagination.total }));
      }
    } catch (err) {
      onShowSnackbar('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openUserProfile = async (twitterUserId) => {
    setSelectedUser(twitterUserId);
    setProfileLoading(true);
    try {
      const [profileRes, journeyRes] = await Promise.all([
        axiosInstance.get(`/api/twitter/users/${twitterUserId}`),
        axiosInstance.get(`/api/twitter/users/${twitterUserId}/journey`)
      ]);
      
      if (profileRes.data.success) setUserProfile(profileRes.data.data);
      if (journeyRes.data.success) setUserJourney(journeyRes.data.data);
    } catch (err) {
      onShowSnackbar('Failed to load user profile', 'error');
    } finally {
      setProfileLoading(false);
    }
  };

  const closeUserProfile = () => {
    setSelectedUser(null);
    setUserProfile(null);
    setUserJourney(null);
  };

  return (
    <Box>
      {/* API Tier Warning */}
      {apiTier === 'free' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Fetching comments requires Twitter Basic tier ($100/mo). User data is populated when you fetch comments from posts.
        </Alert>
      )}

      {/* Summary Cards */}
      {topSummary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{topSummary.total_users}</Typography>
              <Typography variant="body2" color="textSecondary">Total Users</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
              <Typography variant="h4">{topSummary.super_fans}</Typography>
              <Typography variant="body2">Super Fans</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
              <Typography variant="h4">{topSummary.critics}</Typography>
              <Typography variant="body2">Critics</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
              <Typography variant="h4">{topSummary.regulars}</Typography>
              <Typography variant="body2">Regulars</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
              <Typography variant="h4">{topSummary.new_users}</Typography>
              <Typography variant="body2">New Users</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab icon={<TrophyIcon />} label="Top Interactors" iconPosition="start" />
          <Tab icon={<PersonIcon />} label="All Users" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {activeTab === 1 && (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search Username"
                value={usersSearch}
                onChange={(e) => setUsersSearch(e.target.value)}
              />
            </Grid>
          )}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="super_fan">Super Fans</MenuItem>
                <MenuItem value="critic">Critics</MenuItem>
                <MenuItem value="regular">Regulars</MenuItem>
                <MenuItem value="new_user">New Users</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Top Interactors Leaderboard */}
      {activeTab === 0 && (
        <Grid container spacing={2}>
          {loading ? (
            <Grid item xs={12}><CircularProgress /></Grid>
          ) : topInteractors.length === 0 ? (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  No users found. Fetch comments from posts to populate user data.
                </Typography>
              </Paper>
            </Grid>
          ) : (
            topInteractors.map((user, idx) => (
              <Grid item xs={12} md={6} lg={4} key={user.twitter_user_id}>
                <Card 
                  sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
                  onClick={() => openUserProfile(user.twitter_user_id)}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box 
                        sx={{ 
                          width: 40, height: 40, borderRadius: '50%',
                          bgcolor: idx < 3 ? 'warning.main' : 'grey.300',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                      >
                        <Typography variant="h6" color={idx < 3 ? 'white' : 'text.secondary'}>
                          {user.rank}
                        </Typography>
                      </Box>
                      <Avatar src={user.profile_image_url} alt={user.name}>
                        {user.name?.[0] || user.username?.[0]}
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {user.name || user.username}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          @{user.username}
                        </Typography>
                      </Box>
                      <CategoryBadge category={user.user_category} />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">
                          <strong>{user.total_comments}</strong> comments
                        </Typography>
                        <Typography variant="body2" color={user.average_sentiment > 0 ? 'success.main' : 'error.main'}>
                          Sentiment: {(user.average_sentiment * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                      <SentimentBar
                        positive={user.positive_comments || 0}
                        negative={user.negative_comments || 0}
                        neutral={(user.total_comments || 0) - (user.positive_comments || 0) - (user.negative_comments || 0)}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* All Users Table */}
      {activeTab === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="center">Comments</TableCell>
                <TableCell>Sentiment</TableCell>
                <TableCell>First Seen</TableCell>
                <TableCell>Last Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center"><CircularProgress /></TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">No users found</TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.twitter_user_id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={user.profile_image_url} sx={{ width: 32, height: 32 }}>
                          {user.username?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">{user.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            @{user.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell><CategoryBadge category={user.user_category} /></TableCell>
                    <TableCell align="center">{user.total_comments}</TableCell>
                    <TableCell sx={{ width: 150 }}>
                      <SentimentBar
                        positive={user.positive_comments || 0}
                        negative={user.negative_comments || 0}
                        neutral={(user.total_comments || 0) - (user.positive_comments || 0) - (user.negative_comments || 0)}
                      />
                    </TableCell>
                    <TableCell>
                      {user.first_interaction && format(new Date(user.first_interaction), 'MMM d')}
                    </TableCell>
                    <TableCell>
                      {user.last_interaction && format(new Date(user.last_interaction), 'MMM d')}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Profile">
                        <IconButton size="small" onClick={() => openUserProfile(user.twitter_user_id)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={usersPagination.total}
            page={usersPagination.page - 1}
            onPageChange={(e, page) => setUsersPagination(prev => ({ ...prev, page: page + 1 }))}
            rowsPerPage={usersPagination.per_page}
            onRowsPerPageChange={(e) => setUsersPagination(prev => ({ ...prev, per_page: parseInt(e.target.value), page: 1 }))}
          />
        </TableContainer>
      )}

      {/* User Profile Dialog */}
      <Dialog open={!!selectedUser} onClose={closeUserProfile} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">User Profile</Typography>
            <IconButton onClick={closeUserProfile}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {profileLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : userProfile ? (
            <Grid container spacing={3}>
              {/* Profile Header */}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar 
                    src={userProfile.user.profile_image_url} 
                    sx={{ width: 64, height: 64 }}
                  />
                  <Box flex={1}>
                    <Typography variant="h6">{userProfile.user.name}</Typography>
                    <Typography color="textSecondary">@{userProfile.user.username}</Typography>
                    {userProfile.user.bio && (
                      <Typography variant="body2" sx={{ mt: 1 }}>{userProfile.user.bio}</Typography>
                    )}
                  </Box>
                  <CategoryBadge category={userProfile.interaction_stats.user_category} />
                </Box>
              </Grid>

              {/* Stats */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h5">{userProfile.interaction_stats.total_comments}</Typography>
                      <Typography variant="caption">Total Comments</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h5">{userProfile.interaction_stats.posts_interacted_with}</Typography>
                      <Typography variant="caption">Posts Engaged</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="caption" gutterBottom display="block">Sentiment Breakdown</Typography>
                      <SentimentBar
                        positive={userProfile.interaction_stats.positive_comments}
                        negative={userProfile.interaction_stats.negative_comments}
                        neutral={userProfile.interaction_stats.neutral_comments}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>

              {/* Recent Comments */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Recent Comments</Typography>
                <Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <List dense>
                    {userProfile.recent_comments?.map((comment) => (
                      <React.Fragment key={comment.id}>
                        <ListItem>
                          <ListItemText
                            primary={comment.text?.substring(0, 100)}
                            secondary={
                              <Box display="flex" gap={1}>
                                <Chip 
                                  label={comment.sentiment} 
                                  size="small" 
                                  color={comment.sentiment === 'positive' ? 'success' : comment.sentiment === 'negative' ? 'error' : 'default'}
                                />
                                {comment.commented_at && (
                                  <Typography variant="caption">
                                    {format(new Date(comment.commented_at), 'MMM d')}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* User Journey */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Interaction Journey</Typography>
                <Paper sx={{ maxHeight: 300, overflow: 'auto', p: 2 }}>
                  {userJourney?.journey?.map((interaction, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        mb: 2,
                        borderLeft: 3,
                        borderColor: interaction.sentiment === 'positive' ? 'success.main' : 
                                     interaction.sentiment === 'negative' ? 'error.main' : 'grey.400',
                        pl: 2
                      }}
                    >
                      <Box>
                        <Typography variant="body2">{interaction.type}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {interaction.post_text?.substring(0, 50)}...
                        </Typography>
                        <Typography variant="caption" display="block">
                          {interaction.date && format(new Date(interaction.date), 'MMM d, yyyy')}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Typography>Failed to load profile</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UserAnalysis;